import { useNavigate } from 'react-router-dom';
import type { CSSProperties } from 'react';
import type { Stage, Task, TaskEstado } from './types';
import type { Tag, AssigneeReceipt } from './api';

// Pills de estado al estilo de la landing (azul "en curso", verde "hecho").
const ESTADO_PILL: Record<TaskEstado, { label: string; bg: string; color: string }> = {
  en_curso: { label: 'En curso', bg: '#eff5ff', color: '#1d4ed8' },
  resuelto: { label: 'Resuelto', bg: '#fef3c7', color: '#b45309' },
  terminada: { label: 'Terminada', bg: '#dcfce7', color: '#15803d' },
};

interface Props {
  task: Task;
  onClick?: () => void;
  compact?: boolean;
  tags?: Tag[];
  // Acuse de lectura del asignado (estilo WhatsApp). undefined = no pintar tick
  // (hilo sin asignar o que miras tú mismo como responsable).
  readReceipt?: AssigneeReceipt;
  // Hay novedades en el hilo que aún no he visto (vista "Todo" de la Bandeja):
  // borde azul a la izquierda, asunto en negrita y punto junto al remitente.
  unread?: boolean;
  // Etapa del tablero en la que está el hilo: chip junto al estado (solo
  // mientras está en curso; resuelto/terminada mandan sobre la etapa).
  stage?: Stage | null;
}

export default function TaskCard({ task, onClick, compact = false, tags = [], readReceipt, unread = false, stage = null }: Props) {
  const navigate = useNavigate();
  const pill = ESTADO_PILL[task.estado] ?? ESTADO_PILL.en_curso;

  const handleClick = () => {
    if (onClick) onClick();
    else navigate(`/tareas/${task.id}`);
  };

  // Cabecera "De: / Para:" explícita — quien asignó el hilo (bold, arriba) y a
  // quién se lo asignó (secundario, debajo), sin depender de que el color o el
  // orden por sí solos comuniquen el sentido.
  const assignee = task.assignee ?? null;
  const creator = task.creator ?? null;

  let headerPerson: typeof assignee = null;
  let name = 'General';
  let assigneeLabel: string | null = null;

  if (creator) {
    headerPerson = creator;
    name = `De: ${creator.nombre}`;
    assigneeLabel = !assignee
      ? 'Para: General'
      : creator.id !== assignee.id
        ? `Para: ${assignee.nombre}`
        : null;
  } else if (assignee) {
    headerPerson = assignee;
    name = `Para: ${assignee.nombre}`;
  }

  const avatarColor = headerPerson ? headerPerson.color : '#94a3b8';
  const avatarChar = headerPerson ? initial(headerPerson.nombre) : '#';
  const nameColor = headerPerson ? headerPerson.color : '#64748b';

  // El acento de no leído se expresa SIEMPRE con las mismas propiedades
  // concretas (borderLeft / paddingLeft), en vez de añadirlas y quitarlas:
  // React, al quitar una propiedad concreta del objeto style, la vacía en el
  // DOM y no vuelve a aplicar el shorthand (`border`, `padding`), así que la
  // tarjeta se quedaba sin borde izquierdo y sin padding al pasar a leída.
  const cardStyle: CSSProperties = {
    ...styles.card,
    borderLeft: unread ? '3px solid #2563eb' : '1px solid #e2e8f0',
    paddingLeft: unread ? 16 : 18,
  };

  const fechaVisible = task.fecha_vencimiento
    ? formatDate(task.fecha_vencimiento)
    : formatDate(task.created_at);

  const metaParts = [assigneeLabel, fechaVisible || null].filter(Boolean);

  return (
    <button
      type="button"
      onClick={handleClick}
      style={cardStyle}
      onMouseEnter={(e) => {
        e.currentTarget.style.boxShadow = '0 8px 24px rgba(15,23,42,0.10)';
        e.currentTarget.style.transform = 'translateY(-2px)';
        e.currentTarget.style.borderColor = '#cbd5e1';
        if (unread) e.currentTarget.style.borderLeftColor = '#2563eb';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.boxShadow = '0 1px 2px rgba(15,23,42,0.04)';
        e.currentTarget.style.transform = 'none';
        e.currentTarget.style.borderColor = '#e2e8f0';
        if (unread) e.currentTarget.style.borderLeftColor = '#2563eb';
      }}
    >
      <div style={styles.headRow}>
        <span style={{ ...styles.avatar, background: avatarColor }}>{avatarChar}</span>
        <div style={styles.who}>
          <span style={{ ...styles.name, color: nameColor }}>
            {unread && <span style={styles.unreadDot} aria-label="Novedades sin leer" />}
            {name}
          </span>
          {!compact && metaParts.length > 0 && (
            <span style={styles.meta}>{metaParts.join(' · ')}</span>
          )}
        </div>
        {stage && task.estado === 'en_curso' && (
          <span style={styles.stage} title="Etapa">
            <span style={{ ...styles.tagDot, background: stage.color }} />
            {stage.nombre}
          </span>
        )}
        <span style={{ ...styles.pill, background: pill.bg, color: pill.color }}>
          {pill.label}
        </span>
        {readReceipt && (
          <span
            style={styles.receipt}
            title={
              readReceipt.read
                ? `Visto${readReceipt.at ? ` · ${formatSeen(readReceipt.at)}` : ''}`
                : `${assignee?.nombre ?? 'El asignado'} aún no lo ha leído`
            }
            aria-label={readReceipt.read ? 'Leído por el asignado' : 'Sin leer por el asignado'}
          >
            <DoubleCheck color={readReceipt.read ? '#16a34a' : '#cbd5e1'} />
          </span>
        )}
      </div>

      <div style={{ ...styles.subject, ...(unread ? styles.subjectUnread : null) }}>{task.asunto}</div>

      {tags.length > 0 && (
        <div style={styles.tagRow}>
          {tags.map((t) => (
            <span key={t.id} style={styles.tag}>
              <span style={{ ...styles.tagDot, background: t.color }} />
              {t.nombre}
            </span>
          ))}
        </div>
      )}
    </button>
  );
}

// Doble tick estilo WhatsApp: gris = el asignado no lo ha leído, verde = leído.
export function DoubleCheck({ color }: { color: string }) {
  return (
    <svg
      width="20"
      height="15"
      viewBox="0 0 18 14"
      fill="none"
      stroke={color}
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M1 7.4 4.5 11 10.7 3.3" />
      <path d="M6.6 7.4 10.1 11 16.3 3.3" />
    </svg>
  );
}

function initial(nombre: string): string {
  return (nombre.trim()[0] ?? '?').toUpperCase();
}

// "Visto" con fecha + hora corta para el tooltip del tick (p. ej. 21/06 17:15).
function formatSeen(input: string): string {
  const d = new Date(input);
  if (Number.isNaN(d.getTime())) return '';
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const hh = String(d.getHours()).padStart(2, '0');
  const mi = String(d.getMinutes()).padStart(2, '0');
  return `${dd}/${mm} ${hh}:${mi}`;
}

function formatDate(input: string | null | undefined): string {
  if (!input) return '';
  const d = new Date(input);
  if (Number.isNaN(d.getTime())) return '';
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const yy = String(d.getFullYear()).slice(-2);
  return `${dd}/${mm}/${yy}`;
}

const styles: Record<string, CSSProperties> = {
  card: {
    display: 'flex',
    flexDirection: 'column',
    gap: 10,
    width: '100%',
    background: '#fff',
    border: '1px solid #e2e8f0',
    borderRadius: 14,
    padding: '16px 18px',
    cursor: 'pointer',
    textAlign: 'left',
    transition: 'transform .12s ease, box-shadow .15s ease, border-color .15s ease',
    boxShadow: '0 1px 2px rgba(15,23,42,0.04)',
  },
  headRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
  },
  unreadDot: {
    display: 'inline-block',
    width: 8,
    height: 8,
    borderRadius: '50%',
    background: '#2563eb',
    marginRight: 6,
    verticalAlign: 'middle',
    position: 'relative',
    top: -1,
  },
  avatar: {
    width: 32,
    height: 32,
    minWidth: 32,
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#fff',
    fontWeight: 700,
    fontSize: 13,
  },
  who: {
    flex: 1,
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: 1,
  },
  name: {
    fontSize: 13.5,
    fontWeight: 700,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  meta: {
    fontSize: 11.5,
    color: '#94a3b8',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  pill: {
    fontSize: 11,
    fontWeight: 700,
    padding: '3px 10px',
    borderRadius: 999,
    whiteSpace: 'nowrap',
  },
  stage: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 5,
    fontSize: 11,
    fontWeight: 600,
    color: '#475569',
    border: '1px solid #e2e8f0',
    borderRadius: 999,
    padding: '2px 9px',
    whiteSpace: 'nowrap',
  },
  receipt: {
    display: 'inline-flex',
    alignItems: 'center',
    marginLeft: 2,
    flexShrink: 0,
  },
  subject: {
    fontSize: 14.5,
    color: '#1e293b',
    lineHeight: 1.45,
    wordBreak: 'break-word',
    // Recorte a máximo 2 líneas con elipsis.
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
  },
  subjectUnread: {
    fontWeight: 600,
    color: '#0f172a',
  },
  tagRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 6,
  },
  tag: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 5,
    background: '#f1f5f9',
    borderRadius: 999,
    padding: '2px 9px',
    fontSize: 11,
    fontWeight: 600,
    color: '#475569',
  },
  tagDot: {
    width: 7,
    height: 7,
    minWidth: 7,
    borderRadius: '50%',
  },
};
